import React, { useMemo } from 'react';

import Spinner from '@atlaskit/spinner';
import { useJiraContext } from 'src/services/jira';
import { useLinkedIncident } from 'src/services/linkedIncident';
import { useSession } from 'src/services/session';

import integration from 'src/lib/api/integration';
import pd from 'src/lib/api/pagerduty';

import Collapse from 'src/views/components/Collapse';
import ErrorMessage from 'src/views/components/ErrorMessage';

const Note = ({ note }) => {
  const { content, user, created_at } = note;
  const date = new Date(Date.parse(created_at));
  return (
    <div style={{ margin: '0.5em 0' }}>
      <div>
        <a href={user.html_url} target="_blank" rel="noopener noreferrer"
          style={{ fontWeight: 'bold', marginRight: '0.25em' }}>{user.summary}
        </a>
        <span style={{ fontStyle: 'oblique' }}>({date.toLocaleString()}):</span>
      </div>
      {content}
    </div>
  );
};

class Notes extends React.Component {
  state = {
    loading: true,
    notes: undefined,
    error: undefined,
    info: undefined,
  };

  componentDidMount() {
    const {
      jira: {
        settings: { pdApiKey: { value: pdApiKey } }
      },
      session: {
        loggedIn
      },
      incident: { id: incidentId },
      incidentAccountDomain,
      incidentByService,
    } = this.props;

    if (!loggedIn && incidentByService) {
      integration.getIncidentNotes(incidentId)
        .then(notes => this.setState({ loading: false, notes, error: undefined }))
        .catch(error => this.setState({ loading: false, error: ['Can not load notes for incident', error] }));
    } else if (!loggedIn && !pdApiKey) {
      this.setState({
        loading: false,
        info: ['Cannot load Notes', 'Please login or configure PagerDuty REST API Key on the PagerDuty Add-on settings page']
      });
    } else {
      pd.getNotes(incidentId, incidentAccountDomain)
        .then(notes => this.setState({ loading: false, notes, error: undefined }))
        .catch(error => this.setState({ loading: false,
          error: ['Can not load notes for incident', error] }));
    }
  }

  componentWillUnmount() {
    this.setState = () => {};
  }

  render() {
    const { loading, error, info, notes } = this.state;
    const notesArray = (notes && notes.notes) || [];

    if (error) {
      return <ErrorMessage error={error}/>;
    }
    if (info) {
      return <ErrorMessage error={info} appearance="info"/>;
    }
    if (loading) {
      return <div><Spinner/></div>;
    }

    return (
      <div style={{ maxHeight: 200, overflow: 'auto' }} data-cy="notes-content">
        {notesArray.map(note => <Note key={note.id} note={note}/>)}
        {notesArray.length === 0 && <span style={{ fontStyle: 'italic' }}>No Notes</span>}
      </div>
    );
  }
}

const NotesContainer = ({ incident, incidentAccountDomain }) => {
  const session = useSession();
  const jira = useJiraContext();
  const { byService: { incident: incidentByService } } = useLinkedIncident();

  const WithIncident = useMemo(() => () => <Notes incident={incident} incidentAccountDomain={incidentAccountDomain} incidentByService={Boolean(incidentByService)} jira={jira} session={session}/>, [incident, incidentAccountDomain, incidentByService, jira, session]);

  return (
    <div data-cy="notes-trigger" style={{ marginTop: '1rem' }}>
      <Collapse title="Notes"
        persistenceKey="integration_jira_collapse_notes"
        trackEventKey="notes"
        component={WithIncident}/>
    </div>
  );
};

export default NotesContainer;
